import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import Skeleton from '@empiriecom/module-components/Skeleton';

const PageSkeleton = styled.div`
  margin: 0 auto;
  max-width: 1140px;
`;

const PageGridSkeleton = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row nowrap;
  .mobileHeadline {
    display: none;
  }
  .desktopHeadline {
    display: block;
  }
  @media only screen and (max-width: 500px) {
    flex-flow: row wrap;
    .mobileHeadline {
      display: block;
    }
    .desktopHeadline {
      display: none;
    }
  }
`;

const HeadlineSkeleton = styled(Skeleton)`
  margin: 0.75rem 0;
`;

const RatingSkeleton = styled(Skeleton)`
  margin: 0.5rem 0;
  width: 6rem;
`;

const ImageGalleryWrapperSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  width: 63%;
  @media only screen and (max-width: 500px) {
    width: 100%;
  }
`;

const ImageGallerySkeleton = styled(Skeleton)`
  max-width: 660px;
  width: 100%;
  @media only screen and (max-width: 500px) {
    height: 28rem;
  }
`;

const ThumbnailSliderSkeleton = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 1rem;
`;

const ThumbnailSkeleton = styled(Skeleton)`
  width: 6rem;
  height: 6rem;
  margin-right: 1.5rem;
  @media only screen and (max-width: 500px) {
    width: 3rem;
    height: 3rem;
    margin-right: 0.75rem;
  }
`;

const BuyBoxSkeleton = styled.div`
  padding-left: 1rem;
  width: 37%;
  @media only screen and (max-width: 500px) {
    width: 100%;
    padding-left: 0;
  }
`;

const PriceWrapperSkeleton = styled(Skeleton)`
  margin: 1rem auto 0;
`;

const TaxHintSkeleton = styled(Skeleton)`
  font-size: 0.6875rem;
  margin-bottom: 0.25rem;
`;

const InstallmentCalculatorSkeleton = styled(Skeleton)`
  margin: 1.25rem 0;
  padding: 0.5rem 0;
  border-radius: 5px;
  @media only screen and (max-width: 500px) {
    margin: 1.25rem 0.5rem;
  }
`;

const ProductSelectionSkeleton = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  margin: 0.75rem 0;
  width: 100%;
`;

const ProductSelectionHeadlineSkeleton = styled(Skeleton)`
  width: 50%;
  margin-bottom: 1.5rem;
`;

const ProductSelectionTilesSkeleton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProductSelectionTileSkeleton = styled(Skeleton)`
  width: 2.25rem;
  margin: 0.6rem;
`;

const ButtonSkeleton = styled(Skeleton)`
  width: 100%;
  margin-bottom: 0.75rem;
`;

const PageContentSkeleton = styled.div`
  margin-top: 2rem;
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  @media only screen and (max-width: 500px) {
    flex-flow: column wrap;
  }
`;

const CardSkeleton = styled(Skeleton)`
  width: 48.5%;
  margin-bottom: 1rem;
  @media only screen and (max-width: 500px) {
    width: 100%;
  }
`;

/**
 * This is the detail view skeleton. It provides a skeleton view of the detail view
 * elements until all information is loaded and ready to be displayed. It is available
 * for the desktop as well as the mobile view.
 */
export const DetailViewSkeleton: FunctionComponent = (): JSX.Element => (
  <PageSkeleton data-testid="page-skeleton">
    <PageGridSkeleton data-testid="page-grid-skeleton">
      <ImageGalleryWrapperSkeleton>
        <ImageGallerySkeleton height="40rem" />
        <ThumbnailSliderSkeleton data-testid="thumbnail-slider-skeleton">
          <ThumbnailSkeleton height="6rem" />
          <ThumbnailSkeleton height="6rem" />
          <ThumbnailSkeleton height="6rem" />
          <ThumbnailSkeleton height="6rem" />
          <ThumbnailSkeleton height="6rem" />
        </ThumbnailSliderSkeleton>
      </ImageGalleryWrapperSkeleton>
      <div className="mobileHeadline">
        <HeadlineSkeleton height="2rem" />
        <RatingSkeleton height="1rem" />
      </div>
      <BuyBoxSkeleton>
        <div className="desktopHeadline">
          <HeadlineSkeleton height="2rem" />
          <RatingSkeleton height="1rem" />
        </div>
        <PriceWrapperSkeleton height="3rem" />
        <TaxHintSkeleton height="1em" />
        <InstallmentCalculatorSkeleton height="5rem" />
        <ProductSelectionSkeleton data-testid="product-selection-skeleton">
          <ProductSelectionHeadlineSkeleton height="1rem" />
          <ProductSelectionTilesSkeleton>
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
          </ProductSelectionTilesSkeleton>
        </ProductSelectionSkeleton>
        <ProductSelectionSkeleton>
          <ProductSelectionHeadlineSkeleton height="1rem" />
          <ProductSelectionTilesSkeleton>
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
          </ProductSelectionTilesSkeleton>
        </ProductSelectionSkeleton>
        <ProductSelectionSkeleton>
          <ProductSelectionHeadlineSkeleton height="1rem" />
          <ProductSelectionTilesSkeleton>
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
            <ProductSelectionTileSkeleton height="2.25rem" />
          </ProductSelectionTilesSkeleton>
        </ProductSelectionSkeleton>
        <ButtonSkeleton height="1.75rem" />
        <ButtonSkeleton height="1.75rem" />
      </BuyBoxSkeleton>
    </PageGridSkeleton>
    <PageContentSkeleton data-testid="page-content-skeleton">
      <CardSkeleton height="14rem" />
      <CardSkeleton height="14rem" />
    </PageContentSkeleton>
  </PageSkeleton>
);

export default DetailViewSkeleton;
